import React, {FC, useEffect, useMemo, useRef} from 'react';
import {AdRefreshContext, AdRefreshContextValue} from './AdRefreshContext';
import {EventBus} from '@/common/helpers/utils/EventBus';
import {useRoute} from '@/common/services/router';
import {AnyRouteResult} from '@/app/interfaces/routing';
import {RouteName} from '@/app/constants/routing';

const autoRefreshInterval = 60 * 1000;

export const AdRefreshProvider: FC = ({children}) => {
	const {route} = useRoute<AnyRouteResult>();

	const value = useMemo<AdRefreshContextValue>(
		() => ({
			onRefresh: new EventBus<void>(),
		}),
		[],
	);

	// Generate a refresh key, banners should be rendered when the key changes
	let refreshKey = '';

	switch (route.name) {
		case RouteName.mainPage: {
			refreshKey = 'main-age';
			break;
		}
		case RouteName.weather: {
			refreshKey = `f/${route.data.locationId}/${route.data.daysMode || '-'}`;
			break;
		}

		case RouteName.map: {
			refreshKey = `m/${route.data.locationId}/`;
			break;
		}

		default:
			refreshKey = 'not-found-page';
	}

	// Emit refresh on route key change - to update on navigation
	useEffect(() => {
		value.onRefresh.emit();
	}, [refreshKey]);

	// Performs auto-refresh of banners upon user activity after a certain time interval
	const timeoutId = useRef(0);
	const createListenerTimeout = () => {
		timeoutId.current = window.setTimeout(() => {
			const handler = (event: Event) => {
				if (event.type === 'visibilitychange' && document.visibilityState !== 'visible') {
					return;
				}

				document.removeEventListener('mousemove', handler);
				document.removeEventListener('mousedown', handler);
				document.removeEventListener('keydown', handler);
				document.removeEventListener('visibilitychange', handler);
				window.removeEventListener('focus', handler);

				createListenerTimeout();
				value.onRefresh.emit();
			};

			document.addEventListener('mousemove', handler);
			document.addEventListener('mousedown', handler);
			document.addEventListener('keydown', handler);
			document.addEventListener('visibilitychange', handler);
			window.addEventListener('focus', handler);
		}, autoRefreshInterval);
	};

	useEffect(() => {
		createListenerTimeout();
		return () => {
			clearTimeout(timeoutId.current);
		};
	}, [refreshKey]);

	return <AdRefreshContext.Provider value={value}>{children}</AdRefreshContext.Provider>;
};

AdRefreshProvider.displayName = 'AdRefreshProvider';
